// Listing carousel container. Two modes: pass `:listings` (SSR/prefetched), or pass geo props and
// it GETs /api/v/cards/:geoPath/:filterPath on mount via glb/sendRequest. Only the container talks
// to the store; the card (mvtCardProperty) is presentational.
import { mapActions, mapGetters } from 'vuex';
import mvtCardProperty from '@/common/components/mvtcardproperty/mvt-cardproperty.vue';
import mvtShifter from '@/common/components/mvtshifter/mvt-shifter.vue';
import { mapCardListing } from '@/common/store/mapper/_cardListingMapper.js';

export default {
    name: 'mvtListingCarousel',
    components: { mvtCardProperty, mvtShifter },
    // $eventBus is optional — present on pages that use the lazyload/visibility bus (e.g. market-trends).
    inject: { $eventBus: { default: null } },
    emits: ['completed'],
    props: {
        title: { type: String, default: '' },
        // Pre-mapped items (from mapCardsResult in the store) — passed when SSR/prefetched.
        listings: { type: Array, default: null },
        geoPath: { type: String, default: '' },
        filterPath: { type: String, default: 'new-7' },
        geoId: { type: [String, Number], default: '' },
        geoType: { type: String, default: '' },
        geoName: { type: String, default: '' },
        state: { type: String, default: '' },
        pageType: { type: String, default: '' },
        limit: { type: Number, default: 0 },
        theme: { type: String, default: 'style3' },
        // For drop-in replacement of mvtNearby: toggles the page's `eventbus-nearbyList` visibility
        // (showList[listType][listKey]) when there are no results, so empty sections collapse.
        listKey: { type: String, default: '' },
        listType: { type: String, default: '' },
        // Opt-in "No results" message (mirrors mvtNearby) for standalone use. Default off — pages
        // that gate the section via getShowList (e.g. market-trends) collapse instead of showing it.
        showNoResults: { type: Boolean, default: false },
    },
    data() {
        // Seeded from SSR/prefetched props (pre-mapped); overwritten by fetchCards() in client-fetch mode.
        return {
            items: this.listings || [],
            complianceMap: {},
            loading: false,
        };
    },
    computed: {
        ...mapGetters('glb', ['glb']),
        canFetch() {
            return !!(this.geoPath && this.geoId && this.geoType && this.state);
        },
        // SSR items arrive pre-mapped (mapCardsResult); client-fetched items are raw CardListing and need mapping.
        mappedItems() {
            if (this.listings) return this.items;
            return this.items.map((item) => mapCardListing(item, this.complianceMap[item.mls?.id] || null, this.glb.cdnUrl));
        },
    },
    mounted() {
        if (this.canFetch && !this.items.length) this.fetchCards();
        else this.notifyLoaded();
    },
    methods: {
        ...mapActions('glb', ['sendRequest']),
        fetchCards() {
            this.loading = true;
            const url = `${this.glb.appUrl}api/v/cards/${this.geoPath}/${this.filterPath}/`;
            const data = { geoId: this.geoId, geoType: this.geoType, geoName: this.geoName, state: this.state };
            if (this.pageType) data.pageType = this.pageType;
            if (this.limit) data.limit = this.limit;
            return this.sendRequest({ url, data, method: 'GET', requestName: 'listingCarousel' })
                .then((res) => {
                    // Standard API envelope: { data: { totalCount, listings, compliance }, status, version }.
                    const data = res?.data;
                    if (Array.isArray(data?.listings)) {
                        this.items = data.listings;
                        this.complianceMap = data.compliance || {};
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.loading = false;
                    this.notifyLoaded();
                });
        },
        // Preserve the page's lazyload/visibility contract (drop-in for mvtNearby):
        // tell the parent the carousel is ready, and toggle the section's visibility when empty.
        notifyLoaded() {
            this.$emit('completed', this.items);
            if (this.$eventBus && this.listKey) {
                this.$eventBus.$emit('eventbus-nearbyList', this.items, this.items.length, this.listKey, this.listType);
            }
            // Let the shifter recompute arrow visibility once cards are in.
            this.$nextTick(() => this.$refs.shifter && this.$refs.shifter.refresh && this.$refs.shifter.refresh());
        },
    },
};
